import { HttpClient } from '@angular/common/http';
import { Injectable, ModuleWithProviders, OnDestroy } from '@angular/core';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Strings } from 'src/app/common/utility/string/string';
import { Environment } from 'src/environments/environment';
import { StorageService } from '../storage/storage.service';
import { CountryCode, getCountryCode } from './country-code';
import { Language } from './language';


/** 
 * Manages language selection and Translation Module bootstrapping
 */
@Injectable({
  providedIn: 'root'
})
export class TranslationService implements OnDestroy {

  private languageUpdate: Subject<Language> = new Subject();
  private languageChanged: Subject<CountryCode> = new Subject();

  private unsubscribe: Subject<void> = new Subject();
  private static browserLanguage: CountryCode | undefined = undefined;

  private static get BrowserLanguage(): CountryCode | undefined {
    return TranslationService.browserLanguage;
  }
  private static set BrowserLanguage(value: CountryCode | undefined) {
    TranslationService.browserLanguage = value
  }

  private static get configuration(): any {
    return {
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
        defaultLanguage: TranslationService.CurrentLanguage.CountryCode
      }
    }
  }

  public get LanguageChange(): Subject<CountryCode> {
    return this.languageChanged
  }

  public static forRoot(): ModuleWithProviders<TranslateModule> {
    return TranslateModule.forRoot(TranslationService.configuration)
  }

  public static forChild(): ModuleWithProviders<TranslateModule> {
    return TranslateModule.forChild(TranslationService.configuration)
  }

  public get(key: string | Array<string>, interpolateParams?: Object): Observable<string | any> {
    return this.translate.get(key, interpolateParams)
  }

  constructor(
    private translate: TranslateService
  ) {
    this.translate.addLangs(Environment.SupportedLanguages)
    this.translate.setDefaultLang(Environment.DefaultLanguage.CountryCode)
    this.switchLanguage(TranslationService.CurrentLanguage)
    this.processLanguageChange();
    TranslationService.BrowserLanguage = getCountryCode(this.translate.getBrowserLang() ?? Strings.Empty)
  }

  ngOnDestroy(): void {
    this.unsubscribe.next()
    this.unsubscribe.complete();
  }

  public static get CurrentLanguage(): Language {
    const key: CountryCode = StorageService.SelectedLanguage?.CountryCode ?? TranslationService.BrowserLanguage ?? Environment.DefaultLanguage.CountryCode
    return Language.get(key) ?? Language.get(Environment.DefaultLanguage.CountryCode) ?? Language.fallBack();
  }

  public switchLanguage(language: Language): void {
    this.languageUpdate.next(language)
  }

  public setCurrentLanguage(): void {
    this.languageUpdate.next(TranslationService.CurrentLanguage)
  }

  private processLanguageChange(): void {
    this.languageUpdate.pipe(
      takeUntil(this.unsubscribe)
    ).subscribe((language: Language) => {
      StorageService.SelectedLanguage = language
      this.translate.use(language.CountryCode)
      this.languageChanged.next(language.CountryCode);
    })
  }
}

/**
 * Wrapper for AOT compilation instead of TranslateHttpLoader directly
 */
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, Environment.LanguageFiles);
}
